import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';

type DiscoveredRow = {
  id: number;
  host: string;
  first_source_url: string;
  first_discovered_url: string;
  first_seen_at: string;
  probe_status: 'success' | 'failed' | 'unreachable' | 'skipped_tld' | null;
  probe_fetched_at: string | null;
  probe_http_status: number | null;
  page_title: string | null;
  page_description: string | null;
  site_type: string | null;
  covered_by_archive_id: number | null;
  covered_by_archive_domain: string | null;
  first_source_archive_id: number | null;
  first_source_archive_domain: string | null;
  // 0/1 from the API; truthy = hidden from the default list.
  hidden: boolean;
};

type ListResponse = {
  data: DiscoveredRow[];
  meta: { total: number; limit: number; offset: number; site_types: string[] };
};

const PAGE_SIZE = 50;

export default function Discovered() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const q = params.get('q') ?? '';
  const probeStatus = params.get('probe_status') ?? '';
  const siteType = params.get('site_type') ?? '';
  const covered = params.get('covered') ?? '';
  const showHidden = params.get('show_hidden') === '1';
  const page = Math.max(1, Number(params.get('page') ?? '1') | 0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query: Record<string, string | number> = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (q) query.q = q;
    if (probeStatus) query.probe_status = probeStatus;
    if (siteType) query.site_type = siteType;
    if (covered) query.covered = covered;
    if (showHidden) query.show_hidden = 1;
    api<ListResponse>('/discovered', { query })
      .then((resp) => { if (!cancelled) setData(resp); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, probeStatus, siteType, covered, showHidden, page]);

  async function toggleHidden(r: DiscoveredRow) {
    setBusyId(r.id);
    const next = !r.hidden;
    try {
      await api(`/discovered/${r.id}/hide`, { method: 'POST', body: { hidden: next } });
      setData((d) => {
        if (!d) return d;
        // Hiding while hidden rows aren't shown → drop it from the list and
        // adjust the count; otherwise just flip the flag in place.
        if (next && !showHidden) {
          return {
            ...d,
            data: d.data.filter((x) => x.id !== r.id),
            meta: { ...d.meta, total: Math.max(0, d.meta.total - 1) },
          };
        }
        return { ...d, data: d.data.map((x) => (x.id === r.id ? { ...x, hidden: next } : x)) };
      });
    } catch {
      window.alert('Failed to update.');
    } finally {
      setBusyId(null);
    }
  }

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setParams(next);
  }

  function goToPage(p: number) {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page'); else next.set('page', String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const total = data?.meta.total ?? 0;
  const lastPage = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;

  // Options for the site_type filter dropdown. Comes from the response meta;
  // include the active filter value as a fallback so a deep-linked filter
  // still shows the right selection before the first response lands.
  const siteTypes = data?.meta.site_types
    ?? (siteType ? [siteType] : []);

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Discovered external sites</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          External <code>&lt;a href&gt;</code> hosts seen during crawls.
          One row per host (first-discovery only). The probe job fetches
          each host once, extracts <code>&lt;title&gt;</code> + meta
          description, and runs the site-type detection rules.
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <input
            type="search"
            placeholder="Filter by host or title…"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === 'Enter') updateFilter('q', (e.target as HTMLInputElement).value); }}
            style={{ flex: '1 1 240px', minWidth: 0 }}
          />
          <select value={probeStatus} onChange={(e) => updateFilter('probe_status', e.target.value)}>
            <option value="">Any probe status</option>
            <option value="pending">Pending</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="unreachable">Unreachable</option>
            <option value="skipped_tld">Skipped (blocked TLD)</option>
          </select>
          <select value={covered} onChange={(e) => updateFilter('covered', e.target.value)}>
            <option value="">Any coverage</option>
            <option value="yes">Already covered by an archive</option>
            <option value="no">Not yet covered</option>
          </select>
          <select value={siteType} onChange={(e) => updateFilter('site_type', e.target.value)}>
            <option value="">Any site type</option>
            {siteTypes.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => updateFilter('show_hidden', e.target.checked ? '1' : '')}
            />
            <span className="muted">Show hidden</span>
          </label>
        </div>
      </div>

      <div className="card">
        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
        {loading && <p className="muted">Loading…</p>}
        {!loading && data && data.data.length === 0 && (
          <p className="muted">No discovered sites match these filters.</p>
        )}
        {!loading && data && data.data.length > 0 && (
          <>
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              {total.toLocaleString()} discovered host{total === 1 ? '' : 's'}
              {' · '}page {page} of {lastPage}
            </p>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                  <th style={{ padding: '6px 8px' }}>Host</th>
                  <th style={{ padding: '6px 8px' }}>Title / Description</th>
                  <th style={{ padding: '6px 8px' }}>Site type</th>
                  <th style={{ padding: '6px 8px' }}>Probe</th>
                  <th style={{ padding: '6px 8px' }}>Coverage</th>
                  <th style={{ padding: '6px 8px' }}>First seen</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f4f4f4', verticalAlign: 'top', opacity: r.hidden ? 0.5 : 1 }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                      <a href={r.first_discovered_url} target="_blank" rel="noopener noreferrer">{r.host}</a>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                        from{' '}
                        {r.first_source_archive_id ? (
                          <Link to={`/archives/${r.first_source_archive_id}`}>{r.first_source_archive_domain}</Link>
                        ) : '?'}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', maxWidth: 360 }}>
                      <div style={{ fontWeight: 600 }}>{r.page_title || <span className="muted">—</span>}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {r.page_description || <em>(no meta description)</em>}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.site_type ? <code>{r.site_type}</code> : <span className="muted">—</span>}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <ProbeBadge status={r.probe_status} httpStatus={r.probe_http_status} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.covered_by_archive_id ? (
                        <Link to={`/archives/${r.covered_by_archive_id}`} title="An existing archive already covers this host">
                          {r.covered_by_archive_domain}
                        </Link>
                      ) : (
                        <span className="muted">not covered</span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }} className="muted">
                      {new Date(r.first_seen_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="secondary"
                        disabled={busyId === r.id}
                        onClick={() => toggleHidden(r)}
                        title={r.hidden ? 'Show this site in the default list again' : 'Hide this site from the default list'}
                      >
                        {busyId === r.id ? '…' : r.hidden ? 'Unhide' : 'Hide'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {lastPage > 1 && (
              <Pager page={page} lastPage={lastPage} onGo={goToPage} />
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Compact pager with first/last jumps + a windowed page list around the
 *  current page. Same shape as the Search page's pager so the two stay
 *  visually consistent. */
function Pager({ page, lastPage, onGo }: { page: number; lastPage: number; onGo: (p: number) => void }) {
  const windowSize = 2;
  const start = Math.max(2, page - windowSize);
  const end = Math.min(lastPage - 1, page + windowSize);
  const items: (number | '…')[] = [1];
  if (start > 2) items.push('…');
  for (let i = start; i <= end; i++) items.push(i);
  if (end < lastPage - 1) items.push('…');
  if (lastPage > 1) items.push(lastPage);

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(1)} title="First page">« First</button>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(page - 1)}>Prev</button>
      {items.map((it, idx) =>
        it === '…' ? (
          <span key={`g${idx}`} className="muted" style={{ padding: '0 4px' }}>…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={it === page ? '' : 'secondary'}
            onClick={() => onGo(it)}
            style={{ minWidth: 32 }}
          >
            {it}
          </button>
        ),
      )}
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(page + 1)}>Next</button>
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(lastPage)} title="Last page">Last »</button>
    </div>
  );
}

function ProbeBadge({ status, httpStatus }: { status: DiscoveredRow['probe_status']; httpStatus: number | null }) {
  if (status === null) return <span className="muted">pending</span>;
  const color =
    status === 'success' ? '#2a7' :
    status === 'failed' ? '#c63' :
    status === 'skipped_tld' ? '#888' :
    '#c33';
  return (
    <span style={{ color, fontSize: 12 }}>
      {status}{httpStatus ? ` (${httpStatus})` : ''}
    </span>
  );
}
